import { FeatureCollection, LineString } from 'geojson'
import { useMemo } from 'react'
import { Layer, Source } from 'react-map-gl'
import { useSelector } from 'react-redux'
import { selectHighlightedFeatureIdInList, selectMissionTempVisualSettings } from 'store/selectors/mission'
import { ITrajectory } from 'utils/interfaces'

const TransferRoutesOnMap = ({
  transferRoutes,
  hasSelection: hasSelectionProp = false,
  highlightedName,
}: {
  transferRoutes: ITrajectory[]
  hasSelection?: boolean
  highlightedName?: string
}) => {
  const highlightedFeatureIdInList = useSelector(selectHighlightedFeatureIdInList)

  const isRouteHighlighted = (r: any): boolean => {
    if (r.properties?.highlighted) return true
    if (highlightedName && r.properties?.name === highlightedName) return true
    if (highlightedFeatureIdInList != null) {
      if (r.id === highlightedFeatureIdInList) return true
      if (r.properties?.id === highlightedFeatureIdInList) return true
      if (r.properties?.zone_id === highlightedFeatureIdInList) return true
      if (r.properties?.id_on_map === highlightedFeatureIdInList) return true
      if (r.properties?.name === highlightedFeatureIdInList) return true
    }
    return false
  }

  const effectiveHasSelection = hasSelectionProp || highlightedFeatureIdInList != null || !!highlightedName

  const transferRoutesFeatureCollections: FeatureCollection<LineString> = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: transferRoutes.map(r => ({
        ...r,
        properties: {
          ...r.properties,
          highlighted: isRouteHighlighted(r),
        },
      })),
    }
  }, [transferRoutes, highlightedFeatureIdInList, highlightedName])

  const visualSettings = useSelector(selectMissionTempVisualSettings)

  const isHighlightedExpr: any = useMemo(
    () =>
      highlightedName
        ? ['any', ['==', ['get', 'highlighted'], true], ['==', ['get', 'name'], highlightedName]]
        : ['==', ['get', 'highlighted'], true],
    [highlightedName]
  )

  const transferRouteStyle = useMemo<any>(() => {
    return {
      id: 'transfer-route-style',
      type: 'line',
      layout: {
        'line-cap': 'round',
      },
      paint: {
        'line-width': ['case', isHighlightedExpr, 5, visualSettings.transfer_route_width],
        'line-color': visualSettings.transfer_route_color,
        'line-opacity': effectiveHasSelection
          ? ['case', isHighlightedExpr, 1.0, 0.4]
          : visualSettings.transfer_route_opacity,
        'line-dasharray': [4, 4],
      },
    }
  }, [visualSettings, effectiveHasSelection, isHighlightedExpr])

  const transferRouteHaloStyle = useMemo<any>(() => {
    return {
      id: 'transfer-route-halo-style',
      type: 'line',
      layout: {
        'line-cap': 'round',
      },
      paint: {
        'line-width': ['case', isHighlightedExpr, 12, 0],
        'line-color': visualSettings.transfer_route_color,
        'line-opacity': effectiveHasSelection ? ['case', isHighlightedExpr, 0.25, 0.0] : 0.1,
      },
    }
  }, [visualSettings, effectiveHasSelection, isHighlightedExpr])

  return (
    <Source id="transfer-routes" type="geojson" data={transferRoutesFeatureCollections}>
      <Layer {...transferRouteStyle} source="transfer-routes" />
      <Layer {...transferRouteHaloStyle} source="transfer-routes" />
    </Source>
  )
}

export default TransferRoutesOnMap
